"use client";
import { useRef, useState, useEffect } from "react";
import usePublicContacts, { phoneTel, phoneDisplay } from '@/hooks/usePublicContacts';
import { motion } from "framer-motion";
import Image from "next/image";
import { Play, PhoneCall, ArrowRight, CheckCircle2, BarChart3, Users, Clock, ShieldCheck } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";

export default function CTASection() {
  const contacts = usePublicContacts();
  const [isMobile, setIsMobile] = useState(false);
  const t = useT();

  useEffect(() => {
    setIsMobile(
      /Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent) ||
      window.matchMedia("(pointer: coarse)").matches
    );
  }, []);

  return (
    <section id="demo" className="w-full pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-12 lg:pb-16 relative overflow-hidden bg-gradient-to-b from-[#EFF3FE] via-[#F4F7FF] to-[#EFF3FE]">
      {/* ── Minimal background geometry ──────────────────────────────────
          Two flat layers, both masked so they fade out before reaching the
          headline and buttons. They sit under the orbs below, which is what
          keeps them reading as texture rather than pattern. Only two: the
          left column is dense with copy, so there is exactly one empty
          corner and one empty margin to work with. */}

      {/* Dot lattice, fading in from the bottom-left corner — the one region
          the copy doesn't occupy */}
      <div
        aria-hidden
        className="absolute inset-0 lp-dots pointer-events-none
                   [mask-image:radial-gradient(ellipse_62%_58%_at_0%_100%,black,transparent_72%)]"
      />

      {/* Concentric rings echoing the photo blob. Sized well past the 440px
          circle so only the outer arcs clear it, and lg-only — below that the
          layout stacks and the rings drift away from the photo entirely. */}
      <div
        aria-hidden
        className="hidden lg:block absolute top-1/2 right-[-8%] w-[820px] h-[820px] -translate-y-1/2
                   rounded-full lp-rings pointer-events-none
                   [mask-image:radial-gradient(circle,transparent_26%,black_42%,transparent_74%)]"
      />

      {/* Background Lighting Orbs */}
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.05, 0.08, 0.05] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 right-0 w-[550px] h-[550px] bg-[#3D5EE1] rounded-full blur-[140px] pointer-events-none"
      />
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.04, 0.07, 0.04] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 left-0 w-[450px] h-[450px] bg-[#60A5FA] rounded-full blur-[120px] pointer-events-none"
      />

      <div className="site-container relative z-10">
        
        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* LEFT COLUMN: Text & Buttons */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            
            {/* Heading */}
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="font-heading font-light text-3xl sm:text-4xl lg:text-[48px] text-[#0d1117] tracking-tight leading-[1.3] mb-4"
            >
              {t("cta.headingLine1")}
              <span className="block text-[#3D5EE1] mt-1 sm:mt-2">{t("cta.headingLine2")}</span>
            </motion.h2>

            {/* Green Underline Bar */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ width: "96px" }}
              style={{ originX: 0 }}
              className="w-16 h-1 rounded-full bg-[#3D5EE1] mb-6 transition-all duration-300"
            />

            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-600 font-medium leading-relaxed mb-8 max-w-[500px]"
            >
              {t("cta.bodyLine1")} <br />
              {t("cta.bodyLine2")}
            </motion.p>

            {/* Action Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap items-center gap-4 mb-10 w-full sm:w-auto"
            >
              <motion.a
                whileHover={{ scale: 1.04, y: -3, boxShadow: "0 14px 30px rgba(61,94,225,0.35)" }}
                whileTap={{ scale: 0.96 }}
                href="/contact"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl bg-[#3D5EE1] text-white font-sora font-bold text-sm sm:text-base shadow-md transition-all duration-300 group cursor-pointer"
              >
                <Play size={16} className="fill-current group-hover:scale-110 transition-transform" />
                <span>{t("cta.btnDemo")}</span>
                <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform" />
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.04, y: -3, backgroundColor: "#EFF3FE", borderColor: "#3D5EE1" }}
                whileTap={{ scale: 0.96 }}
                href={isMobile ? `tel:${phoneTel(contacts.supportPhone)}` : "/contact"}
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl border border-[#3D5EE1] text-[#3D5EE1] bg-white font-sora font-bold text-sm sm:text-base transition-all duration-300 group cursor-pointer"
              >
                <PhoneCall size={16} className="group-hover:rotate-12 transition-transform" />
                <span>{t("cta.btnSales")}</span>
              </motion.a>
            </motion.div>

            {/* 3 Bottom Feature Badges */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap items-center gap-4 sm:gap-6 pt-6 border-t border-slate-200/80 text-xs sm:text-sm text-slate-600 font-medium"
            >
              <motion.div whileHover={{ scale: 1.06, x: 3 }} className="flex items-center gap-2 cursor-default group">
                <ShieldCheck size={16} className="text-[#3D5EE1] group-hover:scale-110 transition-transform" />
                <span className="group-hover:text-[#3D5EE1] transition-colors">{t("cta.badgeNoCard")}</span>
              </motion.div>
              <span className="hidden sm:inline text-slate-300">|</span>
              <motion.div whileHover={{ scale: 1.06, x: 3 }} className="flex items-center gap-2 cursor-default group">
                <Clock size={16} className="text-[#3D5EE1] group-hover:scale-110 transition-transform" />
                <span className="group-hover:text-[#3D5EE1] transition-colors">{t("cta.badgeTrial")}</span>
              </motion.div>
              <span className="hidden sm:inline text-slate-300">|</span>
              <motion.div whileHover={{ scale: 1.06, x: 3 }} className="flex items-center gap-2 cursor-default group">
                <CheckCircle2 size={16} className="text-[#3D5EE1] group-hover:scale-110 transition-transform" />
                <span className="group-hover:text-[#3D5EE1] transition-colors">{t("cta.badgeCancel")}</span>
              </motion.div>
            </motion.div>

          </div>

          {/* RIGHT COLUMN: Female Administrator Photo & Floating Interactive Badges */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5 flex items-center justify-center relative"
          >
            
            {/* Soft Mint Rounded Circle Blob */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="w-[280px] h-[280px] sm:w-[400px] sm:h-[400px] lg:w-[440px] lg:h-[440px] rounded-full bg-gradient-to-br from-[#EFF3FE] via-[#D6E0FC] to-[#EFF3FE] relative flex items-center justify-center shadow-inner overflow-hidden group"
            >
              {/* Administrator Photo — the source is a 1:1 image with its own
                  backdrop, so filling the 1:1 circle keeps it undistorted and
                  edge-to-edge. object-top keeps her face in frame while the
                  circle mask trims the lower body. */}
              <Image
                src="/cta-administrator-blue.png"
                alt={t("cta.photoAlt")}
                fill
                sizes="(max-width: 640px) 280px, (max-width: 1024px) 400px, 440px"
                className="object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              {/* No `priority` here. This section is the last one on the home
                  page, and `priority` made Next emit a <link rel="preload"> for
                  the photo in the document head, ahead of the render-blocking
                  stylesheets. On a throttled mobile connection that ~19KB was
                  competing with the CSS and the hero font for bandwidth before
                  the headline could paint. Without it the image lazy-loads like
                  every other below-the-fold picture, with the same margin, so
                  it is on screen by the time the visitor scrolls to it. */}
            </motion.div>

            {/* Both floating badges: no Tailwind transition-all. framer-motion drives
                their y/rotate loop by rewriting the inline transform every frame,
                and a CSS transition on the same element turned each of those
                writes into a brand-new 150ms transition: ~60 per second per
                badge, forever, even while off-screen. A Lighthouse trace showed
                2.3s of style/layout work and a 300ms forced reflow from just
                these two nodes. The hover scale/rotate/shadow are tweened by
                framer via whileHover, so nothing here needed a CSS transition;
                the motion itself is unchanged. */}
            {/* Top-Left Floating Badge: Analytics */}
            <motion.div
              animate={{ y: [-8, 8, -8], rotate: [-2, 2, -2] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              whileHover={{ scale: 1.25, rotate: 12, boxShadow: "0 14px 30px rgba(61,94,225,0.25)" }}
              className="absolute top-6 left-4 sm:top-10 sm:left-6 bg-white p-3.5 sm:p-4 rounded-full shadow-lg border border-slate-100/90 text-[#3D5EE1] flex items-center justify-center z-20 cursor-pointer"
            >
              <BarChart3 size={24} />
            </motion.div>

            {/* Bottom-Right Floating Badge: Parents & Students */}
            <motion.div
              animate={{ y: [8, -8, 8], rotate: [2, -2, 2] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              whileHover={{ scale: 1.25, rotate: -12, boxShadow: "0 14px 30px rgba(61,94,225,0.25)" }}
              className="absolute bottom-10 right-4 sm:bottom-12 sm:right-6 bg-white p-4 sm:p-4.5 rounded-full shadow-lg border border-slate-100/90 text-[#3D5EE1] flex items-center justify-center z-20 cursor-pointer"
            >
              <Users size={24} />
            </motion.div>

          </motion.div>

        </div>

      </div>
    </section>
  );
}

